import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Cross,
  Sprout,
  Quote,
  Heart,
  Mic,
} from "lucide-react";
import { brand, founder, books, FOUNDER_PHOTO, FOUNDER_SIGNATURE } from "../mock";

const iconMap = { Cross, BookOpen, Sprout, Mic };

const AboutPage = () => {
  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 bg-[#04181a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Cross className="w-3.5 h-3.5" />
            <span>A Christian-based company</span>
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05] max-w-4xl">
            Built on faith.{" "}
            <em className="text-teal-300 font-serif font-normal">
              For the Kingdom.
            </em>
          </h1>
          <p className="mt-5 text-white/65 text-lg max-w-2xl">
            Good Fruit is a Christian-based platform created to help believers
            flourish spiritually and financially — with community, biblical
            finance education, and capital for the call.
          </p>
        </div>
      </section>

      {/* Founder Card */}
      <section id="founder" className="bg-white text-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Meet the founder
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            A minister, an author,{" "}
            <em className="text-teal-700 font-serif font-normal">
              and a disciple-maker.
            </em>
          </h2>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Photo */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden border border-black/10 bg-black aspect-[4/5] max-w-md mx-auto lg:mx-0">
                <img
                  src={FOUNDER_PHOTO}
                  alt={founder.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-center block"
                />
              </div>
              <div className="mt-5 max-w-md mx-auto lg:mx-0">
                <div className="text-black font-semibold tracking-tight text-lg">
                  {founder.name}
                </div>
                <div className="text-teal-700 text-sm font-medium mt-0.5">
                  {founder.title}
                </div>
              </div>
            </div>

            {/* Bio + credentials */}
            <div className="lg:col-span-7">
              <p className="text-xl md:text-2xl text-black/75 leading-relaxed font-serif italic">
                &ldquo;{founder.shortBio}&rdquo;
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {founder.credentials.map((c) => {
                  const Icon = iconMap[c.icon];
                  return (
                    <div
                      key={c.label}
                      className="rounded-2xl border border-black/10 bg-[#f6faf9] p-5 hover:border-teal-600/40 hover:-translate-y-0.5 transition-all"
                    >
                      <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-600/20 flex items-center justify-center text-teal-700">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="mt-4 font-semibold text-black">
                        {c.label}
                      </div>
                      <div className="mt-1 text-sm text-black/55">{c.sub}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Letter */}
      <section className="bg-[#04181a] text-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-400 font-medium">
            A letter from the founder
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
            On bearing{" "}
            <em className="text-teal-300 font-serif font-normal">
              good fruit.
            </em>
          </h2>

          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-12">
            <Quote className="w-10 h-10 text-teal-300/50" />
            <div className="mt-6 space-y-5 text-white/85 text-base md:text-lg leading-[1.85] font-serif">
              <p>Friend,</p>
              <p>
                I want to be honest with you about why this app exists. I&apos;m
                a licensed and ordained minister. I&apos;ve spent years in the
                training room, in pews, and at kitchen tables — walking with people
                who love Jesus but feel stuck. Stuck in debt. Stuck in jobs
                that don&apos;t fit their calling. Stuck wondering whether the
                dream God gave them was real, or whether they were supposed to
                just be content where they are.
              </p>
              <p>
                I&apos;ve also written two books on financial literacy and
                Kingdom growth, because I&apos;m convinced the Church
                doesn&apos;t talk about money the way the Bible does. Scripture
                mentions money more than two thousand times — not because God
                is obsessed with it, but because we are. Money reveals what we
                worship. And too many believers worship a god of scarcity when
                the God we serve is the God of more-than-enough.
              </p>
              <p>
                <span className="text-teal-300 font-semibold not-italic">
                  Good Fruit is my answer to what I kept seeing.
                </span>{" "}
                A Christian community needs more than another social platform.
                It needs a place where the gospel, real-life finances, and the
                courage to step into your calling all live under one roof.
                That&apos;s what we&apos;re building.
              </p>
              <p>
                The biblical finance course is the same content I&apos;ve
                taught from the training room and in my books — now in your pocket,
                free for members. Leap of Faith Funding exists because
                I&apos;ve watched too many Christian entrepreneurs walk away
                from God-given ideas because no bank would meet with them. We
                won&apos;t replace the bank — but we will sit with you, pray
                with you, and put real capital behind real callings.
              </p>
              <p>
                My mission is simple: to be good fruit and a disciple for
                Christ — and to help you do the same. Whether you&apos;re a
                pastor, a parent, an entrepreneur, or someone just trying to
                get out of debt and live with more freedom — you&apos;re in
                the right place.
              </p>
              <p className="text-teal-300 font-semibold not-italic">
                Welcome to Good Fruit. Let&apos;s grow His Kingdom together.
              </p>
              <div className="pt-4">
                <div className="text-white font-semibold not-italic">
                  In Christ,
                </div>
                <div className="mt-5 inline-flex items-center justify-center rounded-2xl bg-white/95 p-3 shadow-[0_10px_40px_-10px_rgba(20,184,166,0.4)]">
                  <img
                    src={FOUNDER_SIGNATURE}
                    alt={`${founder.name} signature logo`}
                    loading="lazy"
                    decoding="async"
                    width="2000"
                    height="2000"
                    className="h-28 md:h-32 w-auto select-none pointer-events-none"
                  />
                </div>
                <div className="mt-4 text-white/55 text-sm not-italic">
                  {founder.name} · Founder, Good Fruit App
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-teal-300/80 text-lg md:text-xl font-serif italic">
              {brand.verse}
            </p>
          </div>
        </div>
      </section>

      {/* Books */}
      <section
        id="books"
        className="bg-white text-black py-20 md:py-28 border-b border-black/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Two-time published author
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            The{" "}
            <em className="text-teal-700 font-serif font-normal">
              Dope Boy Fresh
            </em>{" "}
            series.
          </h2>
          <p className="mt-5 text-lg text-black/60 max-w-2xl">
            Two books, one mission — wake up young men trapped between fast
            money and lasting freedom, and walk them into the abundance God has
            for them. Available on Amazon.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {books.map((b, i) => (
              <article
                key={b.id}
                className="group relative rounded-2xl overflow-hidden bg-black border border-black/10 hover:-translate-y-1 transition-all duration-300 hover:shadow-[0_25px_60px_-25px_rgba(20,184,166,0.4)]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-5">
                  {/* Cover-style art */}
                  <div
                    className={`sm:col-span-2 relative aspect-[3/4] sm:aspect-auto bg-gradient-to-br ${b.accent} flex items-center justify-center p-7 overflow-hidden`}
                  >
                    <div
                      className="absolute inset-0 opacity-30 mix-blend-overlay"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 30% 20%, #ffffff 0%, transparent 45%), radial-gradient(circle at 80% 80%, #000000 0%, transparent 50%)",
                      }}
                    />
                    <div className="relative text-center">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">
                        {b.tag}
                      </div>
                      <div className="mt-3 font-serif italic text-white/90 text-sm">
                        {b.series}
                      </div>
                      <div className="mt-3 h-px w-12 bg-white/40 mx-auto" />
                      <div className="mt-4 text-white text-xl md:text-2xl font-semibold leading-tight tracking-tight">
                        {b.title}
                      </div>
                      <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-white/70">
                        Tyree A. Huddleston III
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="sm:col-span-3 p-7 md:p-8 text-white flex flex-col">
                    <div className="text-xs uppercase tracking-wide text-teal-300 font-medium">
                      {b.tag} · {b.series}
                    </div>
                    <h3 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
                      {b.title}
                    </h3>
                    <p className="mt-4 text-white/65 leading-relaxed flex-1">
                      {b.description}
                    </p>
                    <a
                      href={b.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-flex items-center gap-2 self-start bg-teal-400 hover:bg-teal-300 text-black font-semibold rounded-md h-11 px-5 transition-colors"
                    >
                      <BookOpen className="w-4 h-4" />
                      Find it on Amazon
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {i === 0 && (
                  <span className="absolute top-4 right-4 bg-teal-400 text-black text-[10px] font-semibold px-2.5 py-1 rounded-full tracking-wider uppercase">
                    Vol. 1
                  </span>
                )}
                {i === 1 && (
                  <span className="absolute top-4 right-4 bg-white text-black text-[10px] font-semibold px-2.5 py-1 rounded-full tracking-wider uppercase">
                    Vol. 2
                  </span>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white text-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Our values
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            What we believe.{" "}
            <em className="text-teal-700 font-serif font-normal">
              What we build by.
            </em>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {founder.values.map((v, i) => (
              <article
                key={v.title}
                className={`rounded-2xl p-7 md:p-8 border transition-all duration-300 hover:-translate-y-1 ${
                  i % 2 === 0
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-black/10 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.2)]"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-2xl font-semibold tracking-tight">
                    {v.title}
                  </h3>
                  <span
                    className={`text-xs font-mono px-2.5 py-1 rounded-full whitespace-nowrap ${
                      i % 2 === 0
                        ? "bg-teal-400/15 text-teal-300 border border-teal-400/30"
                        : "bg-teal-500/10 text-teal-700 border border-teal-600/20"
                    }`}
                  >
                    {v.verse}
                  </span>
                </div>
                <p
                  className={`mt-4 leading-relaxed ${
                    i % 2 === 0 ? "text-white/70" : "text-black/65"
                  }`}
                >
                  {v.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-[#04181a] border border-teal-400/30 p-10 md:p-14 text-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-teal-300 font-medium">
                Walk with us
              </div>
              <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight max-w-2xl mx-auto">
                Three ways to{" "}
                <em className="text-teal-300 font-serif font-normal">
                  bear fruit today.
                </em>
              </h2>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
                <Link to="/#cta">
                  <Button className="w-full bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12">
                    <Sprout className="w-4 h-4 mr-2" /> Plant your Hub
                  </Button>
                </Link>
                <Link to="/finance-course">
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/5 hover:text-teal-300 h-12"
                  >
                    <BookOpen className="w-4 h-4 mr-2" /> Finance course
                  </Button>
                </Link>
                <Link to="/funding">
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/5 hover:text-teal-300 h-12"
                  >
                    <Heart className="w-4 h-4 mr-2" /> Apply for funding
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default AboutPage;
